import { useMemo } from 'react';
import { calculatePortfolioTotalsByMode, type CalculationMode } from '@/lib/portfolioCalculations';

export function usePortfolioTotals(
  effectiveAllocations: any[],
  baseAllocations: any[],
  portfolioSize: number,
  calcMode: CalculationMode,
  overrides: Record<string, number>
) {
  const computedTotals = useMemo(() => (
    calculatePortfolioTotalsByMode(
      effectiveAllocations,
      portfolioSize,
      calcMode
    )
  ), [effectiveAllocations, portfolioSize, calcMode]);

  const persistedTotalsDollar = useMemo(() => (
    calculatePortfolioTotalsByMode(
      baseAllocations,
      portfolioSize,
      'dollar'
    )
  ), [baseAllocations, portfolioSize]);

  const overrideSum = useMemo(() => (
    Object.values(overrides).reduce((s, v) => s + (Number.isFinite(v) ? Number(v) : 0), 0)
  ), [overrides]);

  const fallbackTotals = useMemo(() => {
    if (overrideSum > 0 && computedTotals.totalPercentage === 0) {
      const pct = Math.round(overrideSum * 100) / 100;
      const dollars = Math.round(((pct / 100) * (portfolioSize || 0)) * 100) / 100;
      return {
        totalDollarAmount: dollars,
        totalPercentage: pct,
        isFullyAllocated: Math.abs(pct - 100) < 0.01,
        hasOverflow: dollars > (portfolioSize || 0) + 0.01,
        overflowAmount: Math.max(0, dollars - (portfolioSize || 0))
      };
    }
    return computedTotals;
  }, [overrideSum, computedTotals, portfolioSize]);

  const displayTotals = useMemo(() => (
    calcMode === 'dollar' ? persistedTotalsDollar : fallbackTotals
  ), [calcMode, persistedTotalsDollar, fallbackTotals]);

  const hasOverflowForUi = useMemo(() => (
    calcMode === 'dollar'
      ? (persistedTotalsDollar.hasOverflow || computedTotals.hasOverflow)
      : fallbackTotals.hasOverflow
  ), [calcMode, persistedTotalsDollar, computedTotals, fallbackTotals]);

  return { computedTotals, persistedTotalsDollar, fallbackTotals, displayTotals, hasOverflowForUi, overrideSum } as const;
}
